import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createIssueOrder,
  getOrders,
  getPortalReportHistory,
  manualUpdateCustomerOrderStatus,
  patchIssueOrder,
  retrieveIssueSummary,
  shareIssueToOperation,
} from "../dispatcher/portalDispatcher";

interface PortalState {
  loading: boolean;
  searching: boolean;
  orders: any[];

  loadingAction: boolean;
  loadingModalAction: boolean;
  order: any;

  totalCount: any;
  totalPages: any;
  pageSize: number;
  page: number;
  sort: any;
  status: string;
  searchQuery: string;
  categoryFilter: string;
  subOrganizationFilter: string;
  sortBy: string;
  requestType: string;
  dateFilter: string;

  issues: {
    loading: boolean;
    searching: boolean;
    orders: any[];

    loadingAction: boolean;
    loadingModalAction: boolean;
    order: any;

    totalCount: any;
    totalPages: any;
    pageSize: number;
    page: number;
    sort: any;
    status: string;
    searchQuery: string;
    categoryFilter: string;
    subOrganizationFilter: string;
    sortBy: string;
    issueType: string;
  };
}

const initialState: PortalState = {
  orders: [],
  loading: false,
  searching: false,
  loadingAction: false,

  loadingModalAction: false,

  order: null,

  totalCount: null,
  totalPages: null,
  pageSize: 20,
  page: 1,
  sort: {
    key: "createdAt",
    direction: "desc",
  },
  status: "all",

  // Add new state for search and filters
  searchQuery: "",
  categoryFilter: "all",
  subOrganizationFilter: "all",
  sortBy: "created-desc",
  requestType: "all",
  dateFilter: "Next 14 days",

  issues: {
    orders: [],
    loading: false,
    searching: false,
    loadingAction: false,

    loadingModalAction: false,

    order: null,

    totalCount: null,
    totalPages: null,
    pageSize: 20,
    page: 1,
    sort: {
      key: "createdAt",
      direction: "desc",
    },
    status: "all",

    // Add new state for search and filters
    searchQuery: "",
    categoryFilter: "all",
    subOrganizationFilter: "all",
    sortBy: "created-desc",
    issueType: "all",
  },
};

// Create a slice for actions and reducers
export const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    setOrderSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.page = 1; // reset page when searching
      state.searching = true;
    },
    setOrderCategoryFilter: (state, action) => {
      state.categoryFilter = action.payload;
      state.page = 1;
    },
    setSubOrganizationFilter: (state, action) => {
      state.subOrganizationFilter = action.payload;
      state.page = 1;
    },
    setOrderSortBy: (state, action) => {
      state.sortBy = action.payload;
      state.page = 1;
    },
    setOrderPage: (state, action) => {
      state.page = action.payload;
    },
    setRequestType: (state, action) => {
      state.requestType = action.payload;
    },
    setOrderDateFilter: (state, action) => {
      state.dateFilter = action.payload;
      state.page = 1;
    },

    // Issues Report
    setIssueSearchQuery: (state, action) => {
      state.issues.searchQuery = action.payload;
      state.issues.page = 1; // reset page when searching
      state.issues.searching = true;
    },
    setIssueCategoryFilter: (state, action) => {
      state.issues.categoryFilter = action.payload;
      state.issues.page = 1;
    },
    setIssueSubOrganizationFilter: (state, action) => {
      state.issues.subOrganizationFilter = action.payload;
      state.issues.page = 1;
    },
    setIssueSortBy: (state, action) => {
      state.issues.sortBy = action.payload;
      state.issues.page = 1;
    },
    setIssuePage: (state, action) => {
      state.issues.page = action.payload;
    },
    setIssueType: (state, action) => {
      state.issues.issueType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        if (!state.searching) {
          state.loading = true;
        }
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data.orders;
        state.totalCount = action.payload.data.pagination.total;
        state.totalPages = action.payload.data.pagination.totalPages;
        state.pageSize = action.payload.data.pagination.pageSize;
        state.page = action.payload.data.pagination.page;
        state.searching = false;
      })
      .addCase(getOrders.rejected, (state) => {
        state.loading = false;
        state.searching = false;
      })

      // Get Shipments
      .addCase(getPortalReportHistory.pending, (state) => {
        if (!state.searching) {
          state.issues.loading = true;
        }
      })
      .addCase(getPortalReportHistory.fulfilled, (state, action) => {
        state.issues.loading = false;
        state.issues.orders = action.payload.data.issues;
        state.issues.totalCount = action.payload.data.pagination.total;
        state.issues.totalPages = action.payload.data.pagination.totalPages;
        state.issues.pageSize = action.payload.data.pagination.pageSize;
        state.issues.page = action.payload.data.pagination.page;
        state.issues.searching = false;
      })
      .addCase(getPortalReportHistory.rejected, (state) => {
        state.issues.loading = false;
        state.issues.searching = false;
      })

      // Retrieve Issue Sumamry
      .addCase(retrieveIssueSummary.pending, (state) => {
        state.issues.loading = true;
      })
      .addCase(retrieveIssueSummary.fulfilled, (state, action) => {
        state.issues.loading = false;
        state.issues.order = action.payload.data;
      })
      .addCase(retrieveIssueSummary.rejected, (state) => {
        state.issues.loading = false;
      })

      // Create/Reply Issue Order
      .addCase(createIssueOrder.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(createIssueOrder.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
      })
      .addCase(createIssueOrder.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      // Update Issue Order Status (CLOSE, RE-OPEN)
      .addCase(patchIssueOrder.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(patchIssueOrder.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
        const { _id: issueId, status } = action.payload.data;

        // Find the issue by ID and update its status
        const issueIndex = state.issues.orders.findIndex(
          (issue: any) => issue._id === issueId,
        );
        if (issueIndex !== -1) {
          state.issues.orders[issueIndex].status = status;
        }
      })

      .addCase(patchIssueOrder.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      .addCase(shareIssueToOperation.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(shareIssueToOperation.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
      })
      .addCase(shareIssueToOperation.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      .addCase(manualUpdateCustomerOrderStatus.pending, (state) => {
        state.loadingAction = true;
      })
      .addCase(manualUpdateCustomerOrderStatus.fulfilled, (state, action) => {
        state.loadingAction = false;
      })
      .addCase(manualUpdateCustomerOrderStatus.rejected, (state) => {
        state.loadingAction = false;
      });
  },
});

// Export actions
export const {
  setOrderCategoryFilter,
  setOrderPage,
  setOrderSearchQuery,
  setOrderSortBy,
  setRequestType,
  setOrderDateFilter,
  setSubOrganizationFilter,

  setIssueCategoryFilter,
  setIssuePage,
  setIssueSearchQuery,
  setIssueSortBy,
  setIssueSubOrganizationFilter,
  setIssueType,
} = portalSlice.actions;

// Export reducer
export default portalSlice.reducer;
